import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Sparkles } from "lucide-react";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const [suggestions, setSuggestions] = useState([]);
  const [bookingId, setBookingId] = useState(null);
  const [canSubscribe, setCanSubscribe] = useState(false);
  const [subscribed, setSubscribed] = useState(null);
  const [subBusy, setSubBusy] = useState(false);

  const subscribe = async (frequency) => {
    setSubBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/subscribe`, { frequency });
      setSubscribed(data.frequency);
    } catch { /* keep card visible */ }
    setSubBusy(false);
  };

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("success");
          if (data.booking_id) {
            setBookingId(data.booking_id);
            api.get(`/bookings/${data.booking_id}/suggestions`)
              .then(({ data: s }) => { setSuggestions(s.suggestions || []); setCanSubscribe(!s.is_recurring); })
              .catch(() => {});
          }
          return;
        }
        if (data.status === "expired" || attempts >= 6) return setState("failed");
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= 6) setState("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [params]);

  const M = {
    checking: { icon: <Loader2 className="w-14 h-14 animate-spin text-[#9b111e]" />, t: "Confirming your payment…", d: "Please wait a moment." },
    success: { icon: <CheckCircle2 className="w-14 h-14 text-green-600" />, t: "Payment Confirmed!", d: "Your booking is confirmed. Thank you." },
    failed: { icon: <XCircle className="w-14 h-14 text-amber-600" />, t: "Payment Not Completed", d: "We couldn't confirm payment yet." },
    error: { icon: <XCircle className="w-14 h-14 text-red-600" />, t: "Something went wrong", d: "Please check your dashboard." },
  }[state];

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center">
      <div className="card-metal p-10 flex flex-col items-center gap-4" data-testid="payment-return-status">
        {M.icon}
        <h1 className="font-heading font-black text-2xl text-slate-900">{M.t}</h1>
        <p className="text-slate-600">{M.d}</p>
        <button onClick={() => navigate("/dashboard")} className="btn-red-3d mt-2" data-testid="payment-return-dashboard">
          Go to Dashboard
        </button>
      </div>
      {state === "success" && canSubscribe && (
        <div className="card-metal p-6 mt-6 text-left border-2 border-green-400" data-testid="subscribe-save-card">
          {subscribed ? (
            <div data-testid="subscribe-save-success">
              <h2 className="font-heading font-black text-lg text-green-700">🔁 You're subscribed!</h2>
              <p className="text-sm text-slate-600 mt-1">
                This visit now repeats <b>{subscribed === "biweekly" ? "bi-weekly" : subscribed}</b> — every future auto-booked visit is <b>5% off with NO platform fee</b>. Manage it anytime from your dashboard.
              </p>
            </div>
          ) : (
            <>
              <h2 className="font-heading font-black text-lg text-slate-800">🔁 Subscribe & Save — 5% OFF + NO platform fee</h2>
              <p className="text-sm text-slate-600 mt-1">
                Love this service? Make it repeat automatically and every future visit is <b>5% off with NO platform fee</b> — cancel anytime.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {[["weekly", "Weekly"], ["biweekly", "Bi-weekly"], ["monthly", "Monthly"]].map(([v, label]) => (
                  <button key={v} onClick={() => subscribe(v)} disabled={subBusy}
                    className="btn-red-3d !py-2 !px-4 text-sm" data-testid={`subscribe-${v}`}>
                    {label} · 5% off + no fee
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      {state === "success" && suggestions.length > 0 && (
        <div className="card-metal p-6 mt-6 text-left" data-testid="booking-suggestions">
          <h2 className="font-heading font-black text-lg text-slate-800 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#9b111e]" /> You might also love…
          </h2>
          <p className="text-xs text-slate-500 mt-1">Related services to book in the future — handpicked for you.</p>
          <div className="space-y-2 mt-3">
            {suggestions.map((s, i) => (
              <Link key={i} to={`/provider/${s.provider_id}`}
                className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg p-3 hover:border-[#9b111e] hover:bg-red-50/40 transition-colors"
                data-testid={`suggestion-${i}`}>
                <div>
                  <p className="font-bold text-slate-800">{s.service}</p>
                  <p className="text-xs text-slate-500">
                    with {s.provider_name}
                    {s.reason === "same_pro" ? " · your pro also offers this" : s.rating ? ` · ⭐ ${Number(s.rating).toFixed(1)}/10` : ""}
                  </p>
                </div>
                <span className="font-heading font-black text-[#9b111e] whitespace-nowrap">${Number(s.price).toFixed(0)}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
